import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoCancer = () => {
  const articleSchema = createArticleSchema(
    "Cáncer: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Cáncer. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 21 de junio y el 22 de julio.",
    `${window.location.origin}/horoscopo-cancer`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Cáncer",
        "item": `${window.location.origin}/horoscopo-cancer`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Cáncer" }
  ];

  return (
    <>
      <Helmet>
        <title>Cáncer: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Cáncer. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 21 de junio y el 22 de julio." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-cancer`} />
        <meta property="og:title" content="Cáncer: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Cáncer. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-cancer`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♋</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Cáncer
              </h1>
              <p className="text-xl text-muted-foreground">21 de Junio - 22 de Julio</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Cáncer: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="cancer" signName="Cáncer" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Cáncer: La Madre Nutricia</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Cáncer</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">💧 Elemento: Agua</h4>
                    <p className="text-muted-foreground">Enfoque en las emociones, la intuición y la sensibilidad. Cáncer fluye con las mareas emocionales.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🌊 Modalidad: Cardinal</h4>
                    <p className="text-muted-foreground">Define la necesidad de proteger, iniciar y cuidar. Líder emocional y guardián del hogar.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">🌙 Astro Regente: La Luna</h4>
                    <p className="text-muted-foreground">Símbolo de la emoción, el hogar y el inconsciente. Rige los ciclos emocionales y la memoria.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Cáncer: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Empatía:</strong> Comprensión profunda de las emociones ajenas</li>
                      <li>• <strong>Protección:</strong> Guardián natural de sus seres queridos</li>
                      <li>• <strong>Intuición:</strong> Percepción psíquica altamente desarrollada</li>
                      <li>• <strong>Memoria:</strong> Recuerda detalles emocionales con claridad</li>
                      <li>• <strong>Devoción:</strong> Lealtad inquebrantable</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Hipersensibilidad:</strong> Se hiere fácilmente</li>
                      <li>• <strong>Manipulación:</strong> Puede usar emociones para controlar</li>
                      <li>• <strong>Inseguridad:</strong> Necesita constante reafirmación</li>
                      <li>• <strong>Aferramiento al pasado:</strong> Dificultad para soltar</li>
                      <li>• <strong>Defensividad:</strong> Se retrae en su caparazón</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Siento"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Cáncer en el Amor: Familia, Hogar y Profundidad Emocional</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para Cáncer, el amor es el núcleo de su existencia. Busca crear un hogar seguro donde pueda nutrir y ser nutrido. Su amor es profundo, devoto y protector. Valora la tradición, la familia y los rituales compartidos que crean lazos emocionales duraderos.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Una vez comprometido, Cáncer es increíblemente leal y cuidadoso. Necesita una pareja que valore la intimidad emocional y esté dispuesta a crear un santuario compartido. Su sensibilidad requiere un compañero comprensivo que respete sus cambios emocionales y no lo juzgue por su necesidad ocasional de retirarse a su espacio seguro.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Cáncer en el Trabajo y Finanzas: Emocionalidad en los Negocios</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Cáncer destaca en carreras de cuidado: enfermería, trabajo social, psicología, educación infantil, gastronomía y negocios relacionados con el hogar. Su intuición lo hace excelente en bienes raíces, historia y cualquier campo que requiera memoria emocional y conexión con el pasado.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Cáncer es conservador y orientado a la seguridad. Ahorra para el futuro y tiende a invertir en bienes tangibles, especialmente propiedades. Valora la estabilidad económica como base para la seguridad emocional de su familia. Excelente para construir patrimonio familiar a largo plazo.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Cáncer con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♏ Escorpio y ♓ Piscis</h4>
                    <p className="text-muted-foreground">
                      Los signos de agua comparten profundidad emocional e intuición. Con Escorpio hay intensidad y lealtad mutua. Con Piscis, una conexión espiritual y empática que trasciende las palabras.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♉ Tauro y ♍ Virgo</h4>
                    <p className="text-muted-foreground">
                      Los signos de tierra proporcionan la estabilidad que Cáncer necesita. Tauro ofrece seguridad y sensualidad, mientras que Virgo aporta servicio dedicado y atención a los detalles del cuidado diario.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Cáncer</h3>
                <p className="text-muted-foreground mb-4">
                  La separación y la objetividad son los grandes desafíos de Cáncer. Los signos de aire (Géminis, Libra, Acuario) lo empujan a racionalizar sus emociones y ver las cosas desde una perspectiva más desapegada.
                </p>
                <p className="text-muted-foreground">
                  Capricornio, su signo opuesto, le enseña sobre estructura emocional, límites saludables y el equilibrio entre vida personal y profesional. Los signos de fuego (Aries, Leo, Sagitario) desafían su tendencia a aferrarse, enseñándole sobre independencia y aventura emocional.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoCancer;
